// devToolsApiRouter.js 對應後端 controller/back/admin 的維運工具
// AdminDevTool / AdminSchedule / AdminCache / AdminSocketClient / AdminSession /
// AdminStorage / AdminFlywayHistory / AdminThirdPartyDelivery /
// AdminCallbackForwardConfig / AdminAuditLog / DevToolPlayground
//
// 後端 JSON 端點統一回 CommonResponse（{ status, errorCode, message, data }）；
// service 攔截器已回 response.data（外殼本體），故各呼叫端一律再取一層 res.data
// （storage.download、cache.download、cache.downloadByType 為 blob 例外）。
import service from './service'

export default {
	// 系統維運工具（DevTool）
	devTool: {
		gitVersion: () => service.get(`/back/admin/devTool/gitVersion`),
		systemInfo: () => service.get(`/back/admin/devTool/systemInfo`),
		getRateLimit: () => service.get(`/back/admin/devTool/getRateLimit`),
		updateRateLimit: params =>
			service.put(`/back/admin/devTool/updateRateLimit`, params),
		sendElementMessage: params =>
			service.post(`/back/admin/devTool/sendElementMessage`, params),
		sendLineMessage: params =>
			service.post(`/back/admin/devTool/sendLineMessage`, params),
		listLoggers: () => service.get(`/back/admin/devTool/listLoggers`),
		getLoggerLevel: params =>
			service.get(`/back/admin/devTool/getLoggerLevel`, { params }),
		updateLoggerLevel: params =>
			service.put(`/back/admin/devTool/updateLoggerLevel`, params),
		resetLoggerLevel: params =>
			service.delete(`/back/admin/devTool/resetLoggerLevel`, { params }),
		restoreLoggerLevel: params =>
			service.post(`/back/admin/devTool/restoreLoggerLevel`, null, { params }),
		restoreAllLoggerLevels: () =>
			service.post(`/back/admin/devTool/restoreAllLoggerLevels`),
		allowedDataEnvs: () => service.get(`/back/admin/devTool/allowedDataEnvs`),
		updateStoreDataEnv: params =>
			service.post(`/back/admin/devTool/updateStoreDataEnv`, params),
	},
	// 排程管理（AdminSchedule）：皆作用於處理該請求的節點
	schedule: {
		getSwitch: () => service.get(`/back/admin/schedule/getSwitch`),
		updateSwitch: params =>
			service.post(`/back/admin/schedule/updateSwitch`, params),
		jobList: () => service.get(`/back/admin/schedule/jobList`),
		trigger: params => service.post(`/back/admin/schedule/trigger`, params),
	},

	// 動態面板（Playground）：列出並執行後端所有 @DevToolApi 標記的臨時端點
	// apiList 回 CommonResponse<List<DevToolApiInfo>>（取 res.data）
	// execute：DevToolApiInfo.path 含 /api，而 service baseURL 已含 /api → 去掉開頭 /api 再送
	// （限定後接 /，否則 /apiTest/foo 這類路徑會被誤切成 /Test/foo）
	playground: {
		apiList: () => service.get(`/back/admin/devTool/apiList`),
		execute: ({ method, path, body }) => {
			const url = path.replace(/^\/api(?=\/)/, '')
			const httpMethod = (method || 'GET').toUpperCase()
			if (httpMethod === 'GET') {
				return service.get(url, body ? { params: body } : {})
			}
			return service.request({ method: httpMethod, url, data: body })
		},
	},

	// 快取
	cache: {
		listNames: () => service.get(`/back/admin/cache/listNames`),
		listKeys: params => service.get(`/back/admin/cache/listKeys`, { params }),
		getValue: params => service.get(`/back/admin/cache/getValue`, { params }),
		getByName: params => service.get(`/back/admin/cache/getByName`, { params }),
		// 檢視回 oversized 時改下載完整內容；key 為 Redis 實體鍵（getValue / getByName 回應裡的 key）
		download: params =>
			service.get(`/back/admin/cache/download`, {
				params,
				responseType: 'blob',
			}),
		// cacheKey 省略 = 清空整個 cacheName（高風險），UI 需明確區分
		deleteByName: params =>
			service.delete(`/back/admin/cache/deleteByName`, { params }),
		deleteByPattern: params =>
			service.delete(`/back/admin/cache/deleteByPattern`, { params }),
		batchGet: keys => service.post(`/back/admin/cache/batchGet`, keys),
		batchSet: requests => service.post(`/back/admin/cache/batchSet`, requests),
		batchDelete: keys =>
			service.delete(`/back/admin/cache/batchDelete`, { data: keys }),
		listTypes: () => service.get(`/back/admin/cache/listTypes`),
		getByType: (cacheType, body) =>
			service.post(`/back/admin/cache/getByType`, body, {
				params: { cacheType },
			}),
		downloadByType: (cacheType, body) =>
			service.post(`/back/admin/cache/downloadByType`, body, {
				params: { cacheType },
				responseType: 'blob',
			}),
		reloadByType: (cacheType, body) =>
			service.post(`/back/admin/cache/reloadByType`, body, {
				params: { cacheType },
			}),
		// 本環境未刪除的店家（id + 名稱），供批次重載挑選母體
		listStoreIds: () => service.get(`/back/admin/cache/listStoreIds`),
		// 依型別批次重載多店；params 全批共用，storeId 由後端逐店覆寫
		reloadStoresByType: (cacheType, body) =>
			service.post(`/back/admin/cache/reloadStoresByType`, body, {
				params: { cacheType },
			}),
	},

	// WebSocket 連線（SocketClient）
	socketClient: {
		count: () => service.get(`/back/admin/socketClient/count`),
		currentSidOfHttpSession: params =>
			service.get(`/back/admin/socketClient/currentSidOfHttpSession`, {
				params,
			}),
		touchBySession: params =>
			service.post(`/back/admin/socketClient/touchBySession`, params),
		logout: params => service.post(`/back/admin/socketClient/logout`, params),
		releaseHttpSession: params =>
			service.post(`/back/admin/socketClient/releaseHttpSession`, params),
		cleanupReconnectSet: () =>
			service.post(`/back/admin/socketClient/cleanupReconnectSet`),
		cleanupTempClients: () =>
			service.post(`/back/admin/socketClient/cleanupTempClients`),
		cleanupConnectedClients: () =>
			service.post(`/back/admin/socketClient/cleanupConnectedClients`),
		sendMessageToUser: params =>
			service.post(`/back/admin/socketClient/sendMessageToUser`, params),
		broadcastMessage: params =>
			service.post(`/back/admin/socketClient/broadcastMessage`, params),
	},

	session: {
		findById: sessionId =>
			service.get(`/back/admin/session/${encodeURIComponent(sessionId)}`),
		stompSessionIds: params =>
			service.get(`/back/admin/session/stompSessionIds`, { params }),
		kickOut: params => service.post(`/back/admin/session/kickOut`, params),
		deleteById: sessionId =>
			service.delete(`/back/admin/session/${encodeURIComponent(sessionId)}`),
		kickOutAll: () => service.delete(`/back/admin/session/kickOutAll`),
		cleanupIdleGuestSessions: params =>
			service.delete(`/back/admin/session/cleanupIdleGuestSessions`, {
				data: params,
			}),
	},

	// 存儲
	storage: {
		listCategories: () => service.get(`/back/admin/storage/listCategories`),
		listFiles: params => service.post(`/back/admin/storage/listFiles`, params),
		countFiles: params =>
			service.post(`/back/admin/storage/countFiles`, params),
		exists: params => service.get(`/back/admin/storage/exists`, { params }),
		getFileSize: params =>
			service.get(`/back/admin/storage/getFileSize`, { params }),
		// config 供批次下載傳入 AbortController 的 signal，一般單檔下載不必帶
		download: (params, config = {}) =>
			service.get(`/back/admin/storage/download`, {
				params,
				responseType: 'blob',
				...config,
			}),
		batchUpload: formData =>
			service.post(`/back/admin/storage/batchUpload`, formData),
		delete: params => service.delete(`/back/admin/storage/delete`, { params }),
		batchDelete: params =>
			service.delete(`/back/admin/storage/batchDelete`, { data: params }),
		deleteAll: params =>
			service.delete(`/back/admin/storage/deleteAll`, { data: params }),
	},

	// Flyway 遷移歷史（唯讀）
	flyway: {
		page: params => service.post(`/back/admin/flywayHistory/page`, params),
		getFileContent: params =>
			service.get(`/back/admin/flywayHistory/getFileContent`, { params }),
	},

	// 第三方外送佇列（ThirdPartyDelivery / Uber）
	thirdPartyDelivery: {
		uberOrderQueue: () =>
			service.get(`/back/admin/thirdPartyDelivery/uber/orderQueue`),
		removeUberOrder: params =>
			service.delete(`/back/admin/thirdPartyDelivery/uber/removeOrder`, {
				params,
			}),
		resendUberOrder: params =>
			service.post(`/back/admin/thirdPartyDelivery/uber/resendOrder`, params),
	},

	// 回調轉發配置
	callbackForward: {
		typeList: () => service.get(`/back/admin/callbackForwardConfig/typeList`),
		page: params =>
			service.post(`/back/admin/callbackForwardConfig/page`, params),
		get: params =>
			service.get(`/back/admin/callbackForwardConfig/get`, { params }),
		add: params =>
			service.post(`/back/admin/callbackForwardConfig/add`, params),
		update: params =>
			service.post(`/back/admin/callbackForwardConfig/update`, params),
		updateStatus: params =>
			service.patch(`/back/admin/callbackForwardConfig/updateStatus`, params),
		delete: params =>
			service.delete(`/back/admin/callbackForwardConfig/delete`, { params }),
	},

	// 審計日誌
	auditLog: {
		typeList: () => service.get(`/back/admin/auditLog/typeList`),
		page: params => service.post(`/back/admin/auditLog/page`, params),
		get: params => service.get(`/back/admin/auditLog/get`, { params }),
	},
}
